import React, { useMemo } from 'react';
import { useRecommenders } from '../hooks/useRecommenders';
import { isFullList } from '../lib/fullList';
import PeopleList from './PeopleList';
import Header from './Header';
import Footer from './Footer';
import Skeleton from './Skeleton';

const RecommendersPage: React.FC = () => {
  const { recommenders, isLoaded, error } = useRecommenders();

  const sorted = useMemo(() => {
    // Only full top fives are public. A user who signed up but hasn't
    // finished picking stays off the grid until all five are in — the
    // sign-up page promises exactly that, and partial lists would water
    // down the "everyone here committed to five" framing.
    return recommenders
      .filter((r) => isFullList(r.restaurants.length))
      .slice()
      .sort((a, b) => {
        const aLastName = a.name.split(' ').pop() ?? '';
        const bLastName = b.name.split(' ').pop() ?? '';
        return aLastName.localeCompare(bLastName);
      });
  }, [recommenders]);

  return (
    <div className="min-h-screen bg-cream">
      <Header backTo="/" backLabel="All Restaurants" />

      {/* Hero Section */}
      <div className="pt-16 pb-12 md:pt-24 md:pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl animate-fade-in">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-charcoal mb-6 tracking-tight">
              Our Recommenders
            </h1>
            <p className="text-lg text-charcoal-light font-light leading-relaxed">
              Each person picked only their 5 absolute favorites. No fluff, just the best.
            </p>
          </div>
        </div>
      </div>

      {/* Recommenders Grid */}
      <div className="container mx-auto px-6 pb-16">
        {error ? (
          <p className="text-charcoal-light" role="alert">
            Couldn&rsquo;t load recommenders: {error}
          </p>
        ) : !isLoaded ? (
          <div
            aria-busy="true"
            aria-label="Loading recommenders"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card p-8">
                <div className="flex items-center gap-4 mb-8">
                  <Skeleton className="w-14 h-14 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-5 w-2/3" />
                  </div>
                </div>
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((__, j) => (
                    <div key={j} className="flex items-baseline gap-3">
                      <Skeleton className="h-5 w-4" />
                      <Skeleton className="h-4 flex-1" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <p className="text-charcoal-light">No recommenders yet.</p>
        ) : (
          <PeopleList people={sorted} />
        )}
      </div>

      <Footer />
    </div>
  );
};

export default RecommendersPage;
